import React, { useState, FC } from 'react';
import Calendar from 'react-calendar';
import '../styles/calendar.css';
import { TileClassNameFunc, Value } from 'react-calendar/dist/cjs/shared/types';
import { BinSchedule, RoundType } from '../model/BinTypes';
import { addressToString } from '../utils/addressUtils';
import { roundTypeToNiceString } from '../utils/binUtils';
import { joinWithCommasAnd } from '../utils/listFormatUtils';
import { isSameDate } from '../utils/dateUtils';
import { UpcomingCollectionsProps } from './UpcomingCollections';

type BinColourClass = 'black' | 'green' | 'blue' | 'brown';

const ROUND_TYPE_ORDER: Record<RoundType, number> = {
    DOMESTIC: 0,
    ORGANIC: 1,
    RECYCLE: 2,
    FOOD: 3,
};

function roundTypeToColourClass(roundType: RoundType): BinColourClass {
    switch (roundType) {
        case 'DOMESTIC':
            return 'black';
        case 'ORGANIC':
            return 'green';
        case 'RECYCLE':
            return 'blue';
        case 'FOOD':
            return 'brown';
    }
}

function normalizeRoundTypes(roundTypes: RoundType[]): RoundType[] {
    // Ensure stable ordering + no duplicates (so CSS stays deterministic)
    const unique = Array.from(new Set(roundTypes));
    unique.sort((a, b) => ROUND_TYPE_ORDER[a] - ROUND_TYPE_ORDER[b]);
    return unique;
}

const getTileClassNameFunc = (schedule: BinSchedule) => {
    const tileClassName: TileClassNameFunc = ({ date, view }) => {
        // Check if a date React-Calendar wants to check is one of our collections
        var collection = schedule.collections.find(collection => isSameDate(collection.date, date));

        if (collection !== undefined) {
            const normalizedRoundTypes = normalizeRoundTypes(collection.roundTypes).slice(0, 3);
            const colourClasses = normalizedRoundTypes.map(roundTypeToColourClass);

            const slotClasses = colourClasses.map((c, i) => `bin-${i + 1}-${c}`);
            const countClass = `bin-count-${colourClasses.length}`;

            return `calendar-bin-day ${countClass} ${slotClasses.join(' ')}`;
        }
    }

    return tileClassName;
}

export const UpcomingCollectionsCalendar: FC<UpcomingCollectionsProps> = ({schedule, address}) => {
    const [value, setValue] = useState<Value>(new Date());

    function onChange(nextValue: Value) {
        setValue(nextValue);
    }

    const tileClassNameFunc = getTileClassNameFunc(schedule);

    const selectedDate = Array.isArray(value) ? value[0] : value;
    const selectedDateCollections = selectedDate
        ? schedule.collections.filter(collection => isSameDate(collection.date, selectedDate))
        : [];

    const selectedDateRoundTypes: RoundType[] = normalizeRoundTypes(
        selectedDateCollections.flatMap(c => c.roundTypes)
    );

    const selectedDateBinsText =
        selectedDateRoundTypes.length > 0
            ? joinWithCommasAnd(selectedDateRoundTypes.map(roundTypeToNiceString))
            : null;

    const selectedDateLabel = selectedDate
        ? selectedDate.toLocaleDateString('en-gb', { month: 'long', day: 'numeric' })
        : '';

    const anyRescheduled = selectedDateCollections.some(c => c.slippedCollection);

    return (
        <div className="bin-result bin-result-calendar">
            <h2>{addressToString(address)}</h2>
            <Calendar
                onChange={onChange}
                value={value}
                tileClassName={tileClassNameFunc}
                className='collections-calendar'
                minDetail='month'
                prev2Label={null}
                next2Label={null}
            />
            <div className="calendar-selection" aria-live="polite">
                {selectedDate && selectedDateBinsText && (
                    <p>
                        {selectedDateLabel}
                        {anyRescheduled && ' (RESCHEDULED)'}: {selectedDateBinsText}
                    </p>
                )}
                {selectedDate && !selectedDateBinsText && (
                    <p>{selectedDateLabel}: no collections scheduled.</p>
                )}
            </div>
        </div>
    );
}
